/*
 * Maxilite custom cookie-consent banner (<cookie-consent>).
 *
 * UI only: collects the visitor's choice and registers it with Shopify via
 * customerPrivacy.setTrackingConsent(). That call gates Shopify's pixels AND fires
 * the `visitorConsentCollected` event, which assets/consent-logger.js records for
 * durable proof. This file never POSTs anywhere itself (separation of concerns).
 *
 * Category -> Shopify purpose: Functional -> preferences, Analytics -> analytics,
 * Marketing -> marketing (Marketing also drives sale_of_data in opt-out regions).
 *
 * Reopen anywhere: window.MaxiliteCookieConsent.open()  or a [data-cookie-preferences] element.
 *
 * Debug (testing): add ?cookie_debug=1 to the URL, set localStorage.cookieConsentDebug='1',
 * or window.COOKIE_CONSENT_DEBUG=true. This force-shows the banner anywhere (otherwise it
 * only shows in consent-required regions) and logs lifecycle to the console.
 * window.MaxiliteCookieConsent.reset() clears the consent cookie and reloads for re-testing.
 */
(function () {
  'use strict';

  if (!('customElements' in window)) return;

  var DEBUG = (function () {
    try {
      if (/[?&]cookie_debug=1/.test(window.location.search)) return true;
      if (window.localStorage && localStorage.getItem('cookieConsentDebug') === '1') return true;
    } catch (e) {}
    return window.COOKIE_CONSENT_DEBUG === true;
  })();

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[cookie-consent]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  customElements.define(
    'cookie-consent',
    class CookieConsent extends HTMLElement {
      constructor() {
        super();
        this.bar = this.querySelector('[data-cc-bar]');
        this.overlay = this.querySelector('[data-cc-overlay]');
        this.dialog = this.querySelector('[data-cc-dialog]');
        this.openedBy = null;
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocClick = this.onDocClick.bind(this);
      }

      connectedCallback() {
        this.removeAttribute('hidden');
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocClick);

        var self = this;
        window.MaxiliteCookieConsent = {
          open: function () {
            self.openPreferences();
          },
          close: function () {
            self.hideAll();
          },
          reset: function () {
            self.resetConsent();
          }
        };

        this.initApi();
      }

      initApi() {
        var self = this;
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
          log('Shopify.loadFeatures unavailable — banner stays hidden');
          return;
        }
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          if (error) {
            log('loadFeatures error', error);
            return;
          }
          log(
            'Customer Privacy API ready. region=', self.call('getRegion'),
            'shouldShowBanner=', self.call('shouldShowBanner'),
            'currentConsent=', self.call('currentVisitorConsent')
          );
          self.syncToggles();
          if (self.shouldShow()) self.showBar();
        });
      }

      cp() {
        return (window.Shopify && window.Shopify.customerPrivacy) || null;
      }

      // Read an API value that may be a method or a property, without throwing.
      call(name) {
        try {
          var api = this.cp();
          if (!api) return null;
          var v = api[name];
          return typeof v === 'function' ? v.call(api) : v;
        } catch (e) {
          return null;
        }
      }

      // True once the visitor has recorded any consent decision (yes/no on any purpose).
      hasDecision() {
        // Our own persisted decision first (covers theme dev, where Shopify's cookie may not stick).
        try {
          if (window.localStorage && localStorage.getItem('maxiliteCookieConsent')) return true;
        } catch (e) {}
        var c = this.call('currentVisitorConsent') || {};
        var keys = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
        for (var i = 0; i < keys.length; i++) {
          var v = c[keys[i]];
          if (v === 'yes' || v === 'no' || v === true || v === false) return true;
        }
        return false;
      }

      shouldShow() {
        if (DEBUG) {
          log('debug force-show');
          return true;
        }
        // Show on the first visit to EVERYONE (region-agnostic) until a choice is recorded;
        // once decided, only re-show if Shopify says consent is required again.
        if (!this.hasDecision()) {
          log('no decision yet — showing banner');
          return true;
        }
        return this.call('shouldShowBanner') === true;
      }

      syncToggles() {
        var current = this.call('currentVisitorConsent') || {};
        var inputs = this.querySelectorAll('[data-cc-purpose]');
        Array.prototype.forEach.call(inputs, function (input) {
          var val = current[input.getAttribute('data-cc-purpose')];
          input.checked = val === true || val === 'yes';
        });
      }

      // ---- visibility ----
      showBar() {
        if (this.overlay) this.overlay.setAttribute('hidden', '');
        if (this.bar) this.bar.removeAttribute('hidden');
        log('banner shown');
      }

      openPreferences(opener) {
        this.openedBy = opener || document.activeElement;
        this.syncToggles();
        if (this.bar) this.bar.setAttribute('hidden', '');
        if (this.overlay) this.overlay.removeAttribute('hidden');
        document.body.classList.add('overflow-hidden');
        if (typeof window.trapFocus === 'function') {
          window.trapFocus(this.overlay, this.dialog);
        } else if (this.dialog) {
          this.dialog.focus();
        }
        log('preferences dialog opened');
      }

      hideAll() {
        if (this.bar) this.bar.setAttribute('hidden', '');
        if (this.overlay) this.overlay.setAttribute('hidden', '');
        document.body.classList.remove('overflow-hidden');
        if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.openedBy);
      }

      // Closing the dialog must NOT imply consent: fall back to the banner if a
      // decision is still required, otherwise hide everything.
      closeDialog() {
        if (this.overlay) this.overlay.setAttribute('hidden', '');
        document.body.classList.remove('overflow-hidden');
        if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.openedBy);
        if (this.shouldShow()) this.showBar();
      }

      // ---- events ----
      onClick(event) {
        var t = event.target;
        if (t.closest('[data-cc-accept]')) return this.save({ all: true });
        if (t.closest('[data-cc-reject]')) return this.save({ none: true });
        if (t.closest('[data-cc-save]')) return this.save({});
        if (t.closest('[data-cc-customize]')) return this.openPreferences(t);
        if (t.closest('[data-cc-close]')) return this.closeDialog();
        if (t === this.overlay) return this.closeDialog(); // backdrop click
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.overlay && !this.overlay.hasAttribute('hidden')) {
          event.stopPropagation();
          this.closeDialog();
        }
      }

      onDocClick(event) {
        var trigger = event.target.closest && event.target.closest('[data-cookie-preferences]');
        if (trigger) {
          event.preventDefault();
          this.openPreferences(trigger);
        }
      }

      // ---- persist ----
      save(opts) {
        var consent;
        if (opts.all) {
          consent = { analytics: true, marketing: true, preferences: true, sale_of_data: true };
        } else if (opts.none) {
          consent = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
        } else {
          var on = (function (root) {
            return function (key) {
              var input = root.querySelector('[data-cc-purpose="' + key + '"]');
              return !!(input && input.checked);
            };
          })(this);
          var marketing = on('marketing');
          consent = {
            analytics: on('analytics'),
            marketing: marketing,
            preferences: on('preferences'),
            sale_of_data: marketing
          };
        }

        var action = opts.all ? 'accept_all' : opts.none ? 'reject_all' : 'save_preferences';
        // Remember the decision ourselves so the banner stays hidden across reloads even when
        // Shopify's cookie doesn't persist (e.g. `shopify theme dev` over HTTP). reset() clears it.
        try {
          localStorage.setItem(
            'maxiliteCookieConsent',
            JSON.stringify({ consent: consent, action: action, ts: new Date().toISOString() })
          );
        } catch (e) {}
        var self = this;
        log('setTrackingConsent', consent, action);
        // Notify our logger on the actual click so proof-logging never depends on
        // Shopify's visitorConsentCollected event (which doesn't fire on `shopify theme
        // dev` over HTTP). De-duped logger-side; the canonical event wins when it fires.
        // `action` records HOW consent was given (accept_all/reject_all/save_preferences).
        try {
          document.dispatchEvent(
            new CustomEvent('maxiliteConsentUpdated', { detail: Object.assign({}, consent, { action: action }) })
          );
        } catch (e) {}
        var api = this.cp();
        if (api && typeof api.setTrackingConsent === 'function') {
          try {
            api.setTrackingConsent(consent, function () {
              log('consent saved (visitorConsentCollected will fire)');
              self.hideAll();
            });
            return;
          } catch (e) {
            log('setTrackingConsent threw', e);
          }
        } else {
          log('customerPrivacy.setTrackingConsent unavailable — hiding banner only');
        }
        this.hideAll();
      }

      // Testing helper: clear Shopify's consent cookie(s) and reload so the
      // first-visit banner shows again. Exposed as MaxiliteCookieConsent.reset().
      resetConsent() {
        var names = ['_tracking_consent', '_cmp_a', '_cmp_consent', '_shopify_consent'];
        var host = window.location.hostname;
        var expire = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        names.forEach(function (n) {
          document.cookie = n + expire;
          document.cookie = n + expire + '; domain=' + host;
          document.cookie = n + expire + '; domain=.' + host;
        });
        try { localStorage.removeItem('maxiliteCookieConsent'); } catch (e) {}
        log('cleared consent cookies + localStorage; reloading', names);
        window.location.reload();
      }
    }
  );
})();
